import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brandmark } from "@/components/Brand";
import { Eye, EyeOff } from "lucide-react";

export default function SetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | ready | invalid
  const [info, setInfo] = useState({});
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/set-password/${token}`)
      .then((r) => { setInfo(r.data); setState("ready"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      setUser(data);
      toast.success("Password set, welcome to Solvana");
      nav(data.role === "contractor" ? "/contractor" : ["staff", "admin"].includes(data.role) ? "/staff" : "/portal");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "40px 24px", background: "var(--paper)" }}>
      <div style={{ width: "100%", maxWidth: 380 }} data-testid="set-password-card">
        <Link to="/" style={{ display: "inline-block", marginBottom: 40 }}><Brandmark fontSize={24} tagline /></Link>

        {state === "checking" && <p style={{ color: "var(--muted)" }}>Checking your invitation…</p>}

        {state === "invalid" && (
          <div data-testid="set-password-invalid">
            <h1 style={{ fontSize: 34, marginBottom: 10 }}>Link expired</h1>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24 }}>This invitation link is invalid or has expired. Please ask Solvana Group to send a new one, or sign in if you already have a password.</p>
            <button className="btn-ink" style={{ width: "100%" }} onClick={() => nav("/login")}>Go to sign in</button>
          </div>
        )}

        {state === "ready" && (
          <>
            <h1 style={{ fontSize: 38, marginBottom: 8 }}>Set your password</h1>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 30 }}>
              {info.name ? `Welcome, ${info.name}. ` : "Welcome. "}Choose a password to access your Solvana portal{info.email ? ` (${info.email})` : ""}.
            </p>
            <form onSubmit={submit}>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <input data-testid="set-password-input" className="inp" style={{ paddingRight: 44, width: "100%" }} type={showPw ? "text" : "password"} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} required />
                <button type="button" onClick={() => setShowPw((s) => !s)} aria-label="Toggle password" style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--muted)", display: "flex", padding: 0 }}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <input data-testid="set-password-confirm" className="inp" style={{ marginBottom: 20 }} type={showPw ? "text" : "password"} placeholder="Confirm password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
              <button data-testid="set-password-submit" type="submit" className="btn-ink" style={{ width: "100%" }} disabled={busy}>
                {busy ? "Please wait…" : "Set password & enter portal"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
